import { ToolbarBorderStyle } from '@y42/canvas-components';
import { FontWeight, TextAlignment } from '@y42/json-schema';
import * as go from 'gojs';
import { GojsShadow } from '../../../models/gojs-shadow.model';
import { borderStyleModelToGojsData } from '../../../utils/border-style-utils';
import { updateGojsFontFamily, updateGojsFontSize, updateGojsFontWeight } from '../../../utils/font-utils';
import { shadowModelToGojsData } from '../../../utils/shadow-utils';
import {
  BACKGROUND_COLOR_PROP,
  BORDER_COLOR_PROP,
  BORDER_STYLE_PROP,
  BORDER_WIDTH_PROP,
  LAYER_NAME_PROP,
  LOCKED_PROP,
  SHADOW_BLUR_PROP,
  SHADOW_COLOR_PROP,
  SHADOW_OFFSET_PROP,
  TEXT_ALIGNMENT_PROP,
  TEXT_COLOR_PROP,
  TEXT_FONT_PROP,
  TEXT_HIGHLIGHT_COLOR_PROP,
  TEXT_PROP,
  Z_ORDER_PROP,
} from '../config/common-prop-names';

export const setProperty = <T>(diagram: go.Diagram, data: go.ObjectData, propName: string, value: T) =>
  diagram.commit(() => diagram.model.set(data, propName, value));

export const updateLockedState = (diagram: go.Diagram, data: go.ObjectData, locked: boolean) =>
  setProperty(diagram, data, LOCKED_PROP, locked);

export const updateShadowData = (diagram: go.Diagram, data: go.ObjectData, shadow: GojsShadow) => {
  const gojsShadow = shadowModelToGojsData(shadow, SHADOW_BLUR_PROP, SHADOW_COLOR_PROP, SHADOW_OFFSET_PROP);
  setProperty(diagram, data, SHADOW_COLOR_PROP, gojsShadow[SHADOW_COLOR_PROP]);
  setProperty(diagram, data, SHADOW_BLUR_PROP, gojsShadow[SHADOW_BLUR_PROP]);
  setProperty(diagram, data, SHADOW_OFFSET_PROP, gojsShadow[SHADOW_OFFSET_PROP]);
};

export const updateBackgroundColor = (diagram: go.Diagram, data: go.ObjectData, color: string) =>
  setProperty(diagram, data, BACKGROUND_COLOR_PROP, color);

export const updateFontFamily = (diagram: go.Diagram, data: go.ObjectData, fontFamily: string) => {
  const gojsFont = updateGojsFontFamily(data, TEXT_FONT_PROP, fontFamily);
  setProperty(diagram, data, TEXT_FONT_PROP, gojsFont);
};

export const updateFontSize = (diagram: go.Diagram, data: go.ObjectData, fontSize: number) => {
  const gojsFont = updateGojsFontSize(data, TEXT_FONT_PROP, fontSize);
  setProperty(diagram, data, TEXT_FONT_PROP, gojsFont);
};

export const updateFontWeight = (diagram: go.Diagram, data: go.ObjectData, fontWeightVal: FontWeight) => {
  const gojsFont = updateGojsFontWeight(data, TEXT_FONT_PROP, fontWeightVal);
  setProperty(diagram, data, TEXT_FONT_PROP, gojsFont);
};

export const updateText = (diagram: go.Diagram, data: go.ObjectData, text: string) =>
  setProperty(diagram, data, TEXT_PROP, text);

export const updateTextColor = (diagram: go.Diagram, data: go.ObjectData, textColor: string) =>
  setProperty(diagram, data, TEXT_COLOR_PROP, textColor);

export const updateLayerName = (diagram: go.Diagram, data: go.ObjectData, layerName: string) =>
  setProperty(diagram, data, LAYER_NAME_PROP, layerName);

export const updateZOrder = (diagram: go.Diagram, data: go.ObjectData, zOrder: number) =>
  setProperty(diagram, data, Z_ORDER_PROP, zOrder);

export const updateTextAlignment = (diagram: go.Diagram, data: go.ObjectData, textAlignmentVal: TextAlignment) =>
  setProperty(diagram, data, TEXT_ALIGNMENT_PROP, textAlignmentVal);

export const updateBorderColor = (diagram: go.Diagram, data: go.ObjectData, color: string) =>
  setProperty(diagram, data, BORDER_COLOR_PROP, color);

export const updateTextHighlightColor = (diagram: go.Diagram, data: go.ObjectData, textHighlightColor: string) =>
  setProperty(diagram, data, TEXT_HIGHLIGHT_COLOR_PROP, textHighlightColor);

export const updateBorderStyleData = (diagram: go.Diagram, data: go.ObjectData, borderStyle: ToolbarBorderStyle) => {
  const gojsBorderStyle = borderStyleModelToGojsData(borderStyle, BORDER_WIDTH_PROP, BORDER_STYLE_PROP);
  setProperty(diagram, data, BORDER_WIDTH_PROP, gojsBorderStyle[BORDER_WIDTH_PROP]);
  setProperty(diagram, data, BORDER_STYLE_PROP, gojsBorderStyle[BORDER_STYLE_PROP]);
};
